import { useEffect, useMemo, useState } from 'react';
import { fadeInLeft } from 'react-animations';
import { Trans, useTranslation } from 'react-i18next';
import { Divider, Flex, Heading, HStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import liveMeetingBackground from '@/assets/images/liveMeetingBackground.svg?url';
import AssetModal from '@/components/AssetModal/AssetModal';
import { UseSlideVariationReturnType } from '@/components/CaseSlide/hooks/useSlideVariation';
import AnamneseItem from '@/components/CaseSlide/items/AnamneseItem';
import { useSlideShowStore } from '@/components/CaseSlide/SlideShowSlide/slideShowData.store';
import { getAnamneseItemFromCase } from '@/features/caseCluster/helpers/getAnamneseItem';
import { useUserAuthenticationContext } from '@/features/userAuth/context/UserAuthenticationContext';
import { UseAnswersBySlideReturnType } from '@/services/hooks/useAnswersBySlide';
import Empty from '@/ui/Empty/Empty';
import { SimpleContentModal } from '@/ui/SimpleContentModal';
import { captureSentry } from '@/utils/helpers/sentryHelper';
import { SlideType } from '@/utils/types/enums';
import { Asset } from '@/utils/types/zod/assetSchema';
import { Slide } from '@/utils/types/zod/slideTypes/slideSchema';
import CaseSlide from '../../../components/CaseSlide/CaseSlide';
import MeetingLeftSidebar from '../MeetingLeftSidebar/MeetingLeftSidebar';
import MeetingTestingModeWarning from '../MeetingTestingModeWarning';
import TopRightItem from '../TopRightItem';
import { getAssetListByRefOrThrow } from '../utils/getAssetByRef';
import { getIndexFirstSlideNotAnamnese } from '../utils/getIndexFirstSlideNotAnamnese';
import { CaseExplorerClientProps } from './CaseExplorerClient';
import { useSlideTitle } from './hooks/useSlideTitle';

export type CaseExplorerViewProps = Omit<CaseExplorerClientProps, 'forcedCurrSlide'> & {
  currSlideVariationDetails: UseSlideVariationReturnType;
  answersForSlideDetails: UseAnswersBySlideReturnType;
  currentSlide: Slide | undefined;
  currentSlideWithVariation: Slide | null;
  slideIndex: number;
  isPreview: boolean;
  userID: string | undefined;
  currentHostAssetID: string | null | undefined;
  shouldShowPinchingAnimation: boolean;
  onPinchingAnimationEnd?: () => void;
  resetPinchAnimationTimer: () => void;
};

const CaseExplorerView = ({
  patientCaseIndex,
  patientCase,
  meeting,
  isHost,
  isPreview,
  currentSlide,
  currentSlideWithVariation,
  slideIndex,
  userID,
  currSlideVariationDetails,
  answersForSlideDetails,
  trackAssetSwitch,
  currentHostAssetID,
  shouldShowPinchingAnimation,
  onPinchingAnimationEnd,
  resetPinchAnimationTimer,
}: CaseExplorerViewProps) => {
  const { t } = useTranslation();
  const fromRight = keyframes`${fadeInLeft}`;

  const [modalAssets, setModalAssets] = useState<Asset[] | undefined>();
  const [modalAssetIndex, setModalAssetIndex] = useState<number>(0);
  const [showPatientProfile, setShowPatientProfile] = useState<boolean>(false);
  const { isAdmin } = useUserAuthenticationContext();

  const anamenseItem = getAnamneseItemFromCase(patientCase);
  const shouldRenderLLXBackground = slideIndex < getIndexFirstSlideNotAnamnese(patientCase);
  const slideTitle = useSlideTitle();
  const slideShowStore = useSlideShowStore();

  const slideForLeftSidebar = useMemo(() => {
    if (currentSlideWithVariation?.type === SlideType.SLIDE_SHOW) {
      return slideShowStore.subSlides[slideShowStore.currentSubSlideIdx];
    }
    return currentSlideWithVariation;
  }, [currentSlideWithVariation, slideShowStore]);

  // --------------------- handlers ------------------------

  const showMoreInfoAssets = (assetRefs: string[]) => {
    const assetList = getAssetListByRefOrThrow(assetRefs, patientCase.assets);
    setModalAssetIndex(0);
    trackAssetSwitch &&
      trackAssetSwitch({
        newAssetID: assetList[0].id,
        assetName: assetList[0].title,
        isLibraryAsset: false,
      });
    setModalAssets(assetList);
    resetPinchAnimationTimer();
  };

  const showLibraryAsset = (assetRef: string | undefined) => {
    if (!slideForLeftSidebar) {
      captureSentry({
        title: 'showLibraryAsset: currentSlide is undefined',
        detail: {
          slideForLeftSidebar,
          assetRef,
          meetingID: meeting?.id,
          patientCaseID: patientCase.id,
          slideIndex,
        },
      });
      return;
    }
    const assetList = getAssetListByRefOrThrow(slideForLeftSidebar.libraryRefs, patientCase.assets);
    let index = assetList.findIndex((item) => item.id === assetRef);
    index = index === -1 ? 0 : index;
    const asset = assetList[index];

    if (trackAssetSwitch) {
      if (assetRef) {
        trackAssetSwitch({
          newAssetID: assetRef,
          assetName: asset.path,
          isLibraryAsset: true,
        });
      } else if (assetList.length > 0) {
        trackAssetSwitch({
          newAssetID: assetList[0].id,
          assetName: assetList[0].path,
          isLibraryAsset: true,
        });
      }
    }
    setModalAssetIndex(index);
    setModalAssets(assetList);
    resetPinchAnimationTimer();
  };

  /*
  NOTE: If the slide changes and the modal assets are still open, we close them
  */

  useEffect(() => {
    // if (previousSlideIndex !== slideIndex) {
    setModalAssets(undefined);
    // }
  }, [slideIndex]);

  // --------------------- effect ------------------------

  /*
  Note: I think this code is useless but I'm not sure so I'm just commenting it before deleting it
  */

  // useEffect(() => {
  //   const filteredModalAssets = currentSlide?.items
  //     .filter((slide) => slide.assets && slide.assets?.length > 0)
  //     .filter((data) =>
  //       data.assets?.filter(
  //         (asset) =>
  //           modalAssets && modalAssets.some((el) => asset.url === el.url)
  //       )
  //     );

  //   if (filteredModalAssets) {
  //     setModalAssets(undefined);
  //   }
  // }, [currentSlide?.id]);

  // --------------------- render ------------------------

  if (!currentSlide || !slideForLeftSidebar) {
    // CASE EMPTY
    return (
      <Flex w="full" justifyContent="center">
        <Empty>{t('noSlideFound')}</Empty>
      </Flex>
    );
  }

  return (
    <Flex direction="column" height="100%" w="full" overflow="hidden" position="relative">
      <Flex justifyContent="space-between">
        <Flex flexDirection="column" padding="25px 20px 10px 60px" backgroundColor="white">
          <Heading
            color="livelinx.purple200"
            fontSize="1.5em"
            animation={`0.7s ${fromRight}`}
            userSelect="none"
          >
            <Trans>{slideTitle}</Trans>
          </Heading>

          <HStack justifyContent="space-between" marginBottom="5px">
            <Flex alignItems="center" gap="20px" marginTop="5px"></Flex>
          </HStack>
        </Flex>
        {slideForLeftSidebar.topRight && (
          <TopRightItem item={slideForLeftSidebar.topRight} slides={patientCase.slides} />
        )}
      </Flex>
      <Divider />
      <Flex
        flex="auto"
        pt="10px"
        backgroundSize="cover"
        flexDirection="column"
        position="relative"
        backgroundColor={shouldRenderLLXBackground ? 'livelinx.purple400' : 'white'}
        backgroundImage={shouldRenderLLXBackground ? liveMeetingBackground : ''}
      >
        {meeting?.isInTesting && <MeetingTestingModeWarning isAdmin={isAdmin} />}
        <CaseSlide
          key={currentSlide.id} // this wont be updated without this...
          // key={currentSlide!.id + '' + answers.length} // this wont be updated without this...
          isHost={isHost}
          caseIndex={patientCaseIndex}
          slide={currentSlide}
          slideIndex={slideIndex}
          onShowAssets={(assetList) => {
            showMoreInfoAssets(assetList);
          }}
          isInViewMode={isPreview}
          assetList={patientCase.assets}
          currentCase={patientCase}
          meeting={meeting}
          userID={userID}
          variationDetails={currSlideVariationDetails}
          answersForSlideDetails={answersForSlideDetails}
          currentHostAssetID={currentHostAssetID}
        />
      </Flex>
      {modalAssets && (
        <AssetModal
          index={modalAssetIndex}
          assetList={modalAssets}
          onAssetChange={(oldAsset: Asset, newAsset: Asset) => {
            trackAssetSwitch &&
              trackAssetSwitch({
                newAssetID: newAsset.id,
                assetName: newAsset.path,
                isLibraryAsset: false,
              });
            resetPinchAnimationTimer();
          }}
          onModalClose={() => {
            trackAssetSwitch && trackAssetSwitch(null);
            setModalAssets(undefined);
          }}
          currentHostAssetID={meeting?.currentAssetID}
          shouldShowHostAsset={meeting?.hostID !== userID}
          shouldShowPinchingAnimation={shouldShowPinchingAnimation}
          onPinchingAnimationEnd={onPinchingAnimationEnd}
        />
      )}
      {anamenseItem && (
        <SimpleContentModal
          isOpen={showPatientProfile}
          onClose={() => setShowPatientProfile(false)}
          content={
            <AnamneseItem
              title={`CASES ${patientCaseIndex + 1}`}
              isWomanCase={anamenseItem.gender === 'woman'}
              subtitle={anamenseItem.title}
              descriptionList={anamenseItem.descriptionList}
            />
          }
        />
      )}
      <MeetingLeftSidebar
        patientCase={patientCase}
        currentSlide={slideForLeftSidebar}
        slideIndex={slideIndex}
        onPatientCaseClick={() => setShowPatientProfile(true)}
        onSupportingDocsClick={() => showLibraryAsset(undefined)}
      />
    </Flex>
  );
};

export default CaseExplorerView;
